import React from 'react';
import { Box, Inline } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import {
  Stack,
  BodyText,
  Touchable,
} from '../../../ui';
import {
  useMount, useResponsiveEffect,
} from '../../../../hooks';
import analytics from '../../../../modules/analytics';
import AboutDiscountsModal from '../AboutDiscountsModal';
import type { DiscountDetails } from '../../../../store/NCDAndDiscountedPros.types';
import { ShoppingTagIcon } from '../../../ui/Icons/ShoppingTagIcon';

export type DiscountBannerProps = {
  providerName?: string | null;
  discountDetails: DiscountDetails;
  renderMoreDetailsCta: (onDismiss: () => void) => React.ReactNode | React.ReactNode[];
};

const DiscountBanner: React.FC<DiscountBannerProps> = ({
  providerName,
  discountDetails,
  renderMoreDetailsCta = () => null,
}: DiscountBannerProps) => {
  const is = useResponsiveEffect();
  const align = (is.mobile || is.tablet) ? 'flex-start' : 'center';
  const discountTextPadding = (is.mobile || is.tablet) ? 28 : 0;
  const [showMoreDetails, setShowMoreDetails] = React.useState<boolean>(false);

  const onShowMoreDetailsModal = React.useCallback(() => {
    analytics.track('incentives_more_details_clicked');
    setShowMoreDetails(true);
  }, []);

  const onDismissModal = React.useCallback(() => {
    analytics.track('incentives_more_details_dismissed');
    setShowMoreDetails(false);
  }, []);

  const discountText = providerName ? `${providerName}'s services`
    : 'when booking an eligible pro';
  const amountOffText = discountDetails.discountPercent ? `${discountDetails.discountPercent}%` : `$${discountDetails.discountAmount}`;

  useMount(() => {
    analytics.track('incentives_banner_shown');
  });

  return (
    <React.Fragment>
      <Inline color={colors.creamSpinach} paddingLeft={16} paddingBottom={4}>
        <Stack
          space={4}
          paddingTop={12}
          paddingBottom={8}
          minWidth="100%"
          maxWidth="100%"
          testID="cip-banner"
          alignItems={align}
        >
          <Inline alignItems="center" space={2}>
            <Box
              paddingRight={8}
            >
              <ShoppingTagIcon
                color={colors.green}
                height={16}
                width={16}
              />
            </Box>
            <Box paddingLeft={1}>
              <BodyText
                color={colors.green}
                textAlign="center"
                weight="bold"
                appearance="normal"
              >
                {amountOffText}
                &nbsp;off&nbsp;
              </BodyText>
            </Box>
            <BodyText color={colors.metal} appearance="normal">
              {discountText}
            </BodyText>
          </Inline>
          <Inline
            space={8}
            alignItems="center"
            paddingLeft={discountTextPadding}
          >
            <BodyText
              appearance="small"
              color={colors.ash}
            >
              Discount applied at checkout
            </BodyText>
            <Touchable onClick={onShowMoreDetailsModal}>
              <BodyText
                appearance="small"
                textDecoration="underline"
                color={colors.ash}
              >
                Learn More
              </BodyText>
            </Touchable>
          </Inline>
        </Stack>
      </Inline>
      {showMoreDetails && (
        <AboutDiscountsModal
          onDismiss={onDismissModal}
          discountDetails={discountDetails}
          renderCTA={() => renderMoreDetailsCta(onDismissModal)}
        />
      )}
    </React.Fragment>
  );
};

export default DiscountBanner;
